/**
 * Which shell a route family renders inside — Phase 02.5.
 *
 * WHY THIS EXISTS. `ClientLayout` already had one route-family branch
 * (`isFullscreenRoute`, for `/auth` and `/connect-wallet`) written as an inline
 * segment comparison. Rather than add a second inline `pathname.split()` check
 * to the same component — the duplicated-predicate pattern Phase 01 existed to
 * end — the branch is declared once here and derived.
 *
 * THE THREE SHELL MODES:
 *
 *   `fullscreen`  The route paints its own full-viewport takeover and the app
 *                 chrome behind it is invisible, so none of it is mounted.
 *   `workspace`   The route brings its own persistent rail alongside the global
 *                 chrome. It keeps EVERYTHING, footer included — see below.
 *   `consumer`    Ordinary browsing. Everything mounts. The default.
 *
 * THE FOOTER IS NOT REMOVED ANYWHERE BY DEFAULT (product decision, 2026-08-01).
 * An earlier revision of this file dropped it on workspace routes because it
 * was being drawn underneath the dashboard's rail. That treated the symptom:
 * the footer belongs on every route that has chrome at all, and the real
 * defect was that nothing told it where the content region ends. The rail now
 * publishes its own width (`--workspace-rail-inset`) and the footer clears it,
 * so the footer sits BETWEEN the two rails instead of under one.
 *
 * ONE NAMED EXCEPTION (product decision, 2026-08-10): `/message`. Messages is
 * an app/workspace surface — a live conversation the user stays inside, not a
 * document the user finishes reading — so a marketing footer following the
 * composer read as the page having ended mid-conversation rather than as a
 * normal page boundary. It keeps every other piece of chrome (navbar,
 * sidebar, mobile tab bar); only the Footer is excluded, via
 * `NO_FOOTER_SEGMENTS` below rather than by widening `FULLSCREEN_SEGMENTS`,
 * which would also take the navbar and sidebar it still needs.
 *
 * WHY MODE ALONE CANNOT DRIVE THE INSET. `/` renders the very same
 * `DashboardContent` — and therefore the same rail — as `/dashboard` does, while
 * being an ordinary public `consumer` route. A URL list would have to name the
 * home page as a workspace to get the inset right, which is false, or leave the
 * home page broken. So the inset is published by the component that renders the
 * rail, not inferred from the path; this module only answers the chrome
 * question.
 *
 * PATHNAME-DERIVED ON PURPOSE. The mode is a pure function of the URL, so it
 * resolves identically during server render and hydration — no client-only
 * toggle and no flash of chrome that then disappears.
 *
 * Edge-safe: no imports, no Node built-ins. Kept next to `protected-routes.ts`
 * for the same reason and under the same constraint.
 */

export type ShellMode = "fullscreen" | "workspace" | "consumer";

/**
 * Routes that render their own full-viewport takeover.
 *
 * The original reasoning, preserved verbatim because it is still load-bearing:
 * these routes own an `h-screen` container and their own background, so
 * mounting the app chrome behind them stacked a 56px navbar the page never
 * asked for *under* that container, pushing the document taller than the
 * viewport and forcing a scrollbar unrelated to the content.
 */
const FULLSCREEN_SEGMENTS = ["auth", "connect-wallet"] as const;

/**
 * Route families that bring their own persistent rail.
 *
 * Deliberately a SEGMENT list, not a prefix list: `/dashboard` is a workspace
 * and so is everything under it, but `/dashboards-are-great` (a hypothetical
 * article slug) is not — a `startsWith` check cannot tell those apart, and the
 * segment comparison is the same shape the fullscreen check already used.
 *
 * This list is INFORMATIONAL — it does not gate the footer and does not drive
 * the rail inset (see the note above about `/` rendering the same rail). It
 * exists so a future shell-level decision has a declared place to read from
 * instead of growing another inline pathname check.
 */
const WORKSPACE_SEGMENTS = ["dashboard", "admin", "studio"] as const;

/**
 * Read the route-family segment out of a locale-prefixed pathname.
 * `/en/dashboard/x` → `dashboard`. `/en` → `""`.
 */
export function routeFamilySegment(pathname: string | null | undefined): string {
  if (!pathname) return "";
  // [0] is the empty string before the leading slash, [1] is the locale.
  return pathname.split("/")[2] ?? "";
}

export function resolveShellMode(pathname: string | null | undefined): ShellMode {
  const segment = routeFamilySegment(pathname);
  if ((FULLSCREEN_SEGMENTS as readonly string[]).includes(segment)) return "fullscreen";
  if ((WORKSPACE_SEGMENTS as readonly string[]).includes(segment)) return "workspace";
  return "consumer";
}

/**
 * Route families that behave as an app/workspace surface rather than a
 * document, and so get no Footer even though they keep the rest of the
 * chrome (navbar, sidebar, mobile tab bar).
 *
 * Deliberately its own list, not folded into `WORKSPACE_SEGMENTS`: that list
 * is informational today and explicitly does not gate the footer (see the
 * note above about `/` sharing the dashboard's rail) — conflating the two
 * would either force every future workspace-shaped route into no-footer, or
 * make `WORKSPACE_SEGMENTS` start gating something it is documented not to.
 */
const NO_FOOTER_SEGMENTS = ["message"] as const;

/**
 * The footer renders wherever chrome renders, with one named exception.
 * `fullscreen` is excluded because those routes mount no chrome at all — the
 * same reason they get no navbar and no sidebar. `NO_FOOTER_SEGMENTS` routes
 * mount every other piece of chrome and are excluded only from the footer
 * itself, because they are an app surface rather than a document (2026-08-10).
 *
 * Workspace routes DO get it. Where it sits is a layout question, answered by
 * `--workspace-rail-inset`, not a visibility one.
 */
export function shellShowsPublicFooter(
  mode: ShellMode,
  pathname?: string | null
): boolean {
  if (mode === "fullscreen") return false;
  const segment = routeFamilySegment(pathname);
  if ((NO_FOOTER_SEGMENTS as readonly string[]).includes(segment)) return false;
  return true;
}

/**
 * The CSS variable a workspace rail publishes so surfaces in the shell's flow —
 * the footer, today — can end where the rail begins instead of running beneath
 * it.
 *
 * Set by whichever component renders the rail, cleared on unmount, and read as
 * `me-(--workspace-rail-inset,0px)`. It is `margin-inline-END` in both locales:
 * the rail is `right-0` in English and `left-0` in Persian, which is the inline
 * end of the content region either way, so one logical property covers both.
 */
export const WORKSPACE_RAIL_INSET_VAR = "--workspace-rail-inset";
